import { useRef, useEffect } from "react";

/*
  Interactive particle field for KANN.
  - Drifting gold/zinc dots
  - Lines connect nearby particles
  - Mouse acts as a gentle gravity well + brightens nearby nodes
*/
export default function ParticleField() {
  const canvasRef = useRef(null);
  const mouse = useRef({ x: -9999, y: -9999, active: false });
  const rafRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let width = 0;
    let height = 0;
    let dpr = Math.min(window.devicePixelRatio || 1, 2);
    let particles = [];

    const GOLD = [219, 150, 13];

    const resize = () => {
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = width * dpr;
      canvas.height = height * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      const target = Math.floor((width * height) / 13000);
      const count = Math.max(40, Math.min(150, target));
      particles = new Array(count).fill(0).map(() => spawn());
    };

    const spawn = () => ({
      x: Math.random() * width,
      y: Math.random() * height,
      vx: (Math.random() - 0.5) * 0.35,
      vy: (Math.random() - 0.5) * 0.35,
      r: Math.random() * 1.8 + 0.6,
      gold: Math.random() < 0.22,
    });

    const step = () => {
      ctx.clearRect(0, 0, width, height);
      const mx = mouse.current.x;
      const my = mouse.current.y;

      for (let i = 0; i < particles.length; i++) {
        const p = particles[i];

        // gentle attraction toward cursor
        if (mouse.current.active) {
          const dx = mx - p.x;
          const dy = my - p.y;
          const dist2 = dx * dx + dy * dy;
          if (dist2 < 200 * 200) {
            const dist = Math.sqrt(dist2) || 1;
            const force = (1 - dist / 200) * 0.35;
            p.vx += (dx / dist) * force * 0.08;
            p.vy += (dy / dist) * force * 0.08;
          }
        }

        p.x += p.vx;
        p.y += p.vy;

        // friction so it doesn't accelerate forever
        p.vx *= 0.985;
        p.vy *= 0.985;

        // keep a base drift
        if (Math.abs(p.vx) < 0.05) p.vx += (Math.random() - 0.5) * 0.05;
        if (Math.abs(p.vy) < 0.05) p.vy += (Math.random() - 0.5) * 0.05;

        // wrap around edges
        if (p.x < -20) p.x = width + 20;
        if (p.x > width + 20) p.x = -20;
        if (p.y < -20) p.y = height + 20;
        if (p.y > height + 20) p.y = -20;

        // brightness boost near cursor
        let glow = 0;
        const ddx = mx - p.x;
        const ddy = my - p.y;
        const d = Math.sqrt(ddx * ddx + ddy * ddy);
        if (mouse.current.active && d < 160) glow = 1 - d / 160;

        const baseAlpha = p.gold ? 0.75 : 0.4;
        const alpha = Math.min(1, baseAlpha + glow * 0.6);
        const color = p.gold
          ? `rgba(${GOLD[0]},${GOLD[1]},${GOLD[2]},${alpha})`
          : `rgba(228,228,231,${alpha})`;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r + glow * 1.6, 0, Math.PI * 2);
        ctx.fillStyle = color;
        ctx.fill();
      }

      // connecting lines
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const a = particles[i];
          const b = particles[j];
          const dx = a.x - b.x;
          const dy = a.y - b.y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            const isGold = a.gold || b.gold;
            const alpha = (1 - dist / 120) * (isGold ? 0.28 : 0.12);
            ctx.strokeStyle = isGold
              ? `rgba(${GOLD[0]},${GOLD[1]},${GOLD[2]},${alpha})`
              : `rgba(161,161,170,${alpha})`;
            ctx.lineWidth = 0.6;
            ctx.beginPath();
            ctx.moveTo(a.x, a.y);
            ctx.lineTo(b.x, b.y);
            ctx.stroke();
          }
        }
      }

      // cursor halo
      if (mouse.current.active) {
        const grad = ctx.createRadialGradient(mx, my, 0, mx, my, 170);
        grad.addColorStop(0, "rgba(219,150,13,0.10)");
        grad.addColorStop(1, "rgba(219,150,13,0)");
        ctx.fillStyle = grad;
        ctx.fillRect(mx - 170, my - 170, 340, 340);
      }

      rafRef.current = requestAnimationFrame(step);
    };

    const onMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouse.current.x = e.clientX - rect.left;
      mouse.current.y = e.clientY - rect.top;
      mouse.current.active = true;
    };
    const onLeave = () => {
      mouse.current.active = false;
      mouse.current.x = -9999;
      mouse.current.y = -9999;
    };
    const onTouch = (e) => {
      if (!e.touches[0]) return;
      const rect = canvas.getBoundingClientRect();
      mouse.current.x = e.touches[0].clientX - rect.left;
      mouse.current.y = e.touches[0].clientY - rect.top;
      mouse.current.active = true;
    };

    resize();
    step();
    window.addEventListener("resize", resize);
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseout", onLeave);
    window.addEventListener("touchmove", onTouch, { passive: true });
    window.addEventListener("touchend", onLeave);

    return () => {
      cancelAnimationFrame(rafRef.current);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseout", onLeave);
      window.removeEventListener("touchmove", onTouch);
      window.removeEventListener("touchend", onLeave);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      data-testid="particle-field-canvas"
      className="particle-canvas"
    />
  );
}
